/* Siloueta — fiches produit : raccord doux entre les photos et le fond.

   Constat mesuré : le thème ne pose ni bordure, ni ombre, ni arrondi autour
   des visuels, et le conteneur est déjà à l'ivoire de la page. La démarcation
   rectangulaire vient donc du fond propre à chaque fichier — un studio quasi
   blanc (247→254) posé sur l'ivoire du site (244 239 232).

   On ne retouche aucun fichier. On adoucit les bords à l'affichage, bord par
   bord, et seulement là où le fond est réellement uni et clair. Un bord où
   arrivent le corps, les cheveux, une bretelle ou un décor reste net : chaque
   photo reçoit donc son propre traitement, jamais un masque uniforme.

   La mesure porte sur la zone VISIBLE (les visuels sont en « cover » : les
   bords affichés ne sont pas ceux du fichier).

   Le zoom, les miniatures et la barre d'achat gardent l'image d'origine.
   Sans script, ou si la mesure échoue, rien n'est appliqué. */
(() => {
  if (window.__silPdpImages) return;
  window.__silPdpImages = true;

  const CIBLES = 'media-gallery .product-media__image, .ed-chapter__media img';
  const EXCLUS = 'dialog, .dialog-zoomed-gallery, .dialog-thumbnails-list, .sticky-add-to-cart';

  const UNI = 18; // variation maximale tolérée le long du bord
  const CLAIR = 232; // plus sombre : décor ou vêtement, on ne touche pas
  const N = 16; // points mesurés par bord
  const S = 110; // finesse de l'échantillon

  let toile;

  const mesurer = (img) => {
    const r = img.getBoundingClientRect();
    if (!r.width || !r.height || !img.naturalWidth) return null;

    const cs = getComputedStyle(img);
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    let sx = 0;
    let sy = 0;
    let sw = nw;
    let sh = nh;

    if (cs.objectFit === 'cover') {
      const e = Math.max(r.width / nw, r.height / nh);
      sw = r.width / e;
      sh = r.height / e;
      const p = cs.objectPosition.split(' ');
      const part = (v) => (v && v.indexOf('%') > -1 ? parseFloat(v) / 100 : 0.5);
      sx = (nw - sw) * part(p[0]);
      sy = (nh - sh) * part(p[1] || p[0]);
    }

    toile = toile || document.createElement('canvas');
    toile.width = S;
    toile.height = S;
    const ctx = toile.getContext('2d', { willReadFrequently: true });
    let d;
    try {
      ctx.clearRect(0, 0, S, S);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, S, S);
      d = ctx.getImageData(0, 0, S, S).data;
    } catch (e) {
      return null; // image d'un autre domaine : on ne touche à rien
    }

    const px = (a, b) => {
      const i = ((b * S) + a) * 4;
      return [d[i], d[i + 1], d[i + 2]];
    };
    const bande = (f) => {
      const pts = [];
      for (let k = 0; k < N; k++) pts.push(f(Math.round(((k + 0.5) / N) * (S - 1))));
      const moy = [0, 1, 2].map((j) => pts.reduce((s, c) => s + c[j], 0) / pts.length);
      let ec = 0;
      pts.forEach((c) => [0, 1, 2].forEach((j) => {
        const v = Math.abs(c[j] - moy[j]);
        if (v > ec) ec = v;
      }));
      return { clair: Math.min(moy[0], moy[1], moy[2]), ec };
    };

    return {
      haut: bande((a) => px(a, 1)),
      bas: bande((a) => px(a, S - 2)),
      gauche: bande((b) => px(1, b)),
      droite: bande((b) => px(S - 2, b)),
      w: r.width,
      h: r.height,
    };
  };

  // Longueur du raccord : nulle si le bord porte du sujet ou un décor.
  // Toujours proportionnelle au visuel affiché, et plafonnée à un dixième
  // du côté : sur une petite vignette, le raccord reste discret.
  const longueur = (bord, cote) => {
    if (bord.ec > UNI || bord.clair < CLAIR) return 0;
    const part = (bord.ec < 6 && bord.clair > 246) ? 0.06 : 0.04;
    const brut = Math.min(44, Math.max(10, part * cote));
    return Math.round(Math.min(brut, cote * 0.1));
  };

  const axe = (sens, debut, fin) => {
    const t = [sens];
    t.push(debut ? 'transparent 0, #000 ' + debut + 'px' : '#000 0');
    t.push(fin ? '#000 calc(100% - ' + fin + 'px), transparent 100%' : '#000 100%');
    return 'linear-gradient(' + t.join(', ') + ')';
  };

  const poser = (img) => {
    const m = mesurer(img);
    if (!m) return;

    const g = longueur(m.gauche, m.w);
    const d = longueur(m.droite, m.w);
    const h = longueur(m.haut, m.h);
    const b = longueur(m.bas, m.h);

    const couches = [];
    if (g || d) couches.push(axe('to right', g, d));
    if (h || b) couches.push(axe('to bottom', h, b));

    if (!couches.length) {
      img.removeAttribute('data-sil-fade');
      img.style.removeProperty('--sil-mask');
      return;
    }
    img.style.setProperty('--sil-mask', couches.join(', '));
    img.setAttribute('data-sil-fade', String(couches.length));
  };

  // Une image n'est mesurable qu'une fois mise en page : au chargement, la
  // galerie n'a pas encore sa largeur. Le ResizeObserver rattrape ce moment,
  // et sert aussi quand la fenêtre change (le recadrage « cover » bouge).
  const enAttente = new Set();
  let minuteur;
  const planifier = (img) => {
    enAttente.add(img);
    clearTimeout(minuteur);
    minuteur = setTimeout(() => {
      const lot = [...enAttente];
      enAttente.clear();
      lot.forEach(poser);
    }, 120);
  };

  const taille = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target.getBoundingClientRect().width > 0) planifier(entry.target);
      });
    })
    : null;

  const suivis = new Set();
  const balayer = () => {
    document.querySelectorAll(CIBLES).forEach((img) => {
      if (suivis.has(img) || img.closest(EXCLUS)) return;
      suivis.add(img);
      if (taille) taille.observe(img);
      if (img.complete && img.naturalWidth) planifier(img);
      else img.addEventListener('load', () => planifier(img), { once: true });
    });
  };

  // Ceinture et bretelles : si l'observateur de taille ne se déclenche pas
  // (recadrage piloté par une requête de conteneur, image en cours d'échange
  // dans un srcset), un passage au redimensionnement remesure tout.
  let attente;
  window.addEventListener('resize', () => {
    clearTimeout(attente);
    attente = setTimeout(() => suivis.forEach(poser), 300);
  });

  // Changement de coloris, galerie reconstruite : de nouvelles images arrivent.
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && (node.matches(CIBLES) || node.querySelector(CIBLES))) {
          balayer();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', balayer);
  else balayer();
  window.addEventListener('load', balayer);
})();
